import React, { useState, useEffect } from 'react';
import { FileText, Download, Check, X, Clock, AlertCircle, Eye } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const CustomerQuotes = () => {
  const { token } = useAuth();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingQuote, setViewingQuote] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadQuotes();
  }, []);

  const loadQuotes = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/customer`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setQuotes(data);
      }
    } catch (error) {
      console.error('Errore caricamento preventivi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAction = async (quoteId, action, notes = '') => {
    if (action === 'reject' && !window.confirm('Sei sicuro di voler rifiutare questo preventivo?')) {
      return;
    }

    setActionLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/customer/${quoteId}/action`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            action,
            customer_notes: notes
          })
        }
      );

      if (response.ok) {
        await loadQuotes();
        setViewingQuote(null);
        alert(action === 'accept' ? '✅ Preventivo accettato!' : action === 'reject' ? '❌ Preventivo rifiutato' : '📝 Richiesta modifica inviata');
      } else {
        const error = await response.json();
        alert(`Errore: ${error.detail}`);
      }
    } catch (error) {
      console.error('Errore azione:', error);
      alert('Errore durante l\'operazione');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDownloadPDF = async (quoteId, quoteNumber) => {
    try {
      console.log('📥 Downloading PDF for quote:', quoteId, 'Token:', token ? 'present' : 'missing');
      
      if (!token) {
        alert('Sessione scaduta. Effettua nuovamente il login.');
        return;
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotes/customer/${quoteId}/download-pdf`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `preventivo_${quoteNumber}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorText = await response.text();
        console.error('PDF download error:', response.status, errorText);
        if (response.status === 401 || response.status === 403) {
          alert('Sessione scaduta. Effettua nuovamente il login.');
        } else {
          alert('Errore durante il download del PDF. Riprova più tardi.');
        }
      }
    } catch (error) {
      console.error('Errore download PDF:', error);
      alert('Errore durante il download del PDF. Verifica la connessione.');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { label: 'In Attesa', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      sent: { label: 'Inviato', color: 'bg-blue-100 text-blue-800', icon: FileText },
      accepted: { label: 'Accettato', color: 'bg-green-100 text-green-800', icon: Check },
      rejected: { label: 'Rifiutato', color: 'bg-red-100 text-red-800', icon: X },
      modification_requested: { label: 'Modifica Richiesta', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
      converted_to_order: { label: 'Convertito in Ordine', color: 'bg-purple-100 text-purple-800', icon: Check },
      expired: { label: 'Scaduto', color: 'bg-gray-100 text-gray-800', icon: Clock }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {config.label}
      </span>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">I Tuoi Preventivi</h2>
        <p className="text-gray-600 mt-1">Visualizza e gestisci i preventivi ricevuti</p>
      </div>

      {quotes.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm p-12 text-center">
          <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun preventivo</h3>
          <p className="text-gray-600">Non hai ancora ricevuto preventivi.</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {quotes.map((quote) => (
            <div key={quote.id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">Preventivo {quote.quote_number}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Emesso il {formatDate(quote.issue_date)} • Valido fino al {formatDate(quote.expiry_date)}
                    </p>
                  </div>
                  {getStatusBadge(quote.status)}
                </div>

                <div className="border-t border-b border-gray-200 py-4 my-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Prodotti:</span>
                    <span className="font-medium">{quote.items.length} voce/i</span>
                  </div>
                  {quote.services && quote.services.length > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Servizi:</span>
                      <span className="font-medium">{quote.services.length} servizio/i</span>
                    </div>
                  )}
                  <div className="flex justify-between text-lg font-bold pt-2 border-t">
                    <span>Totale (IVA incl.):</span>
                    <span className="text-amber-600">{formatCurrency(quote.total)}</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setViewingQuote(quote)}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    Visualizza Dettagli
                  </button>
                  <button
                    onClick={() => handleDownloadPDF(quote.id, quote.quote_number)}
                    className="flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    PDF
                  </button>
                  {quote.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleAction(quote.id, 'accept')}
                        disabled={actionLoading}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        <Check className="w-4 h-4" />
                        Accetta
                      </button>
                      <button
                        onClick={() => handleAction(quote.id, 'reject')}
                        disabled={actionLoading}
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                        Rifiuta
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal Dettaglio */}
      {viewingQuote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold">Preventivo {viewingQuote.quote_number}</h2>
                  <div className="mt-2">{getStatusBadge(viewingQuote.status)}</div>
                </div>
                <button
                  onClick={() => setViewingQuote(null)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-6">
                {/* Date */}
                <div className="grid grid-cols-2 gap-4 border-t pt-4">
                  <div>
                    <p className="text-sm text-gray-600">Data Emissione</p>
                    <p className="font-medium">{formatDate(viewingQuote.issue_date)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Scadenza</p>
                    <p className="font-medium">{formatDate(viewingQuote.expiry_date)}</p>
                  </div>
                </div>

                {/* Prodotti */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-3">Prodotti</h3>
                  <div className="space-y-2">
                    {viewingQuote.items.map((item, index) => {
                      // Parse bottle price from details if present
                      const bottlePriceMatch = item.details?.match(/Bottiglia:.*?\(\+€([\d.,]+)/);
                      const bottleExtraCost = bottlePriceMatch ? parseFloat(bottlePriceMatch[1].replace(',', '.')) : 0;
                      
                      return (
                        <div key={index} className="bg-gray-50 p-3 rounded">
                          <div className="flex justify-between">
                            <div className="flex-1">
                              <p className="font-medium">{item.description}</p>
                              <p className="text-sm text-gray-600">
                                {item.format} × {item.quantity} @ {
                                  item.discount_percent > 0 ? (
                                    <>
                                      <span className="line-through text-gray-400">{formatCurrency(item.unit_price)}</span>
                                      {' '}
                                      <span className="text-green-600 font-semibold">
                                        {formatCurrency(item.unit_price * (1 - item.discount_percent / 100))}
                                      </span>
                                      {' '}
                                      <span className="text-green-600 text-xs">(-{item.discount_percent}% sconto)</span>
                                    </>
                                  ) : (
                                    formatCurrency(item.unit_price)
                                  )
                                }
                              </p>
                              {/* Show bottle premium cost separately if present */}
                              {bottleExtraCost > 0 && (
                                <p className="text-xs text-amber-600 mt-1 font-medium">
                                  🍾 Include costo bottiglia premium: +{formatCurrency(bottleExtraCost)}/unità
                                </p>
                              )}
                              {item.details && (
                                <p className="text-xs text-gray-500 mt-1 italic">{item.details}</p>
                              )}
                            </div>
                            <p className="font-semibold">{formatCurrency(item.total)}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Servizi */}
                {viewingQuote.services && viewingQuote.services.length > 0 && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-3">Servizi Aggiuntivi</h3>
                    <div className="space-y-2">
                      {viewingQuote.services.map((service, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded flex justify-between">
                          <div>
                            <p className="font-medium">{service.description}</p>
                            {service.details && (
                              <p className="text-xs text-gray-500 mt-1 italic">{service.details}</p>
                            )}
                          </div>
                          <p className="font-semibold">{formatCurrency(service.total || service.price)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Totali */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotale (IVA esclusa)</span>
                    <span>{formatCurrency(viewingQuote.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span>IVA 22%</span>
                    <span>{formatCurrency(viewingQuote.vat_amount)}</span>
                  </div>
                  <div className="flex justify-between text-xl font-bold pt-2 border-t">
                    <span>Totale</span>
                    <span className="text-amber-600">{formatCurrency(viewingQuote.total)}</span>
                  </div>
                </div>

                {/* Termini */}
                {viewingQuote.terms_and_conditions && (
                  <div className="border-t pt-4">
                    <h3 className="font-semibold mb-2">Termini e Condizioni</h3>
                    <p className="text-sm text-gray-600">{viewingQuote.terms_and_conditions}</p>
                  </div>
                )}

                {/* Azioni */}
                <div className="border-t pt-4 flex justify-end space-x-3">
                  <button
                    onClick={() => handleDownloadPDF(viewingQuote.id, viewingQuote.quote_number)}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    <span>Scarica PDF</span>
                  </button>
                  {viewingQuote.status === 'pending' && (
                    <>
                      <button
                        onClick={() => handleAction(viewingQuote.id, 'accept')}
                        disabled={actionLoading}
                        className="flex items-center space-x-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        <Check className="w-4 h-4" />
                        <span>Accetta</span>
                      </button>
                      <button
                        onClick={() => handleAction(viewingQuote.id, 'reject')}
                        disabled={actionLoading}
                        className="flex items-center space-x-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50"
                      >
                        <X className="w-4 h-4" />
                        <span>Rifiuta</span>
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => setViewingQuote(null)}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Chiudi
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerQuotes;
